/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import Grid from '@mui/material/Grid';

import WidgetDialog from '../components/dialogs';
import ContentContainer from '../components/contentContainer';
import CopyLinkButton from '../components/copyLinkButton';
import TitleHeader from '../components/titleHeader';
import PrimaryButton from '../components/primaryButton';
import GuideLink from '../components/guidelink';

import { useStyles } from './styles';
import useWidgetTheme from '../../commons/widgetTheme';
import TranslationIds from '../../commons/translationIds';
import useTranslator from '../../commons/translations';
import getCommonUIStyles from '../../commons/uiStyles';
import useICSLink from '../icslink/useICSLink';
import { useAppStore } from '../../store/models/app/';
import usePageTransitions from '../../store/models/animations/usePageTransitions';
import {
    TransitionViewNames,
} from '../../store/models/animations/actions/transitions';

import useKickstarted from '../../commons/hooks/useKickstarted';
import ImportantOutlook from './importantOutlook.jsx';

function ICSLinkOutlook (props) {
    const {
        viewAnimation = false,
        initStyles = {},
    } = props;

    const {
        translate,
        translateWithTags,
    } = useTranslator();

    const {
        widget,
        widgetTheme,
        hasCustomCSS,
    } = useWidgetTheme();

    const [nextEnable, setNextEnable] = useState(false);
    const appStore = useAppStore();
    const {
        calendarURL,
        copyCalendarURL,
        inProgress,
        selectedCalendar,
    } = useICSLink();

    const classes = useStyles({
        inProgress,
        widget,
        widgetTheme,
        hasCustomCSS,
        commonUIStyles: getCommonUIStyles({ widgetTheme, widget }),
    });

    usePageTransitions({
        elementName: TransitionViewNames.ICSLINK_OUTLOOK_VIEW,
        viewAnimation,
    });

    useKickstarted({});

    if (!appStore.kickstarted) {
        return (<React.Fragment></React.Fragment>);
    }

    function addCalendarText () {
        return (
            <b style={{ padding: '0 2px' }}>
                {translate(TranslationIds.CALENDAR_GUIDE_ADD_CALENDAR)}
            </b>
        );
    }

    function subscribeFromWebText () {
        return (
            <b style={{ padding: '0 2px' }}>
                {translate(TranslationIds.CALENDAR_GUIDE_SUBSCRIBE_FROM_WEB)}
            </b>
        );
    }

    function importText () {
        return (
            <b style={{ padding: '0 2px' }}>
                {translate(TranslationIds.CALENDAR_GUIDE_IMPORT)}
            </b>
        );
    }

    return (
        <React.Fragment>
            <ContentContainer viewAnimation={viewAnimation}>
                <WidgetDialog
                    isShowBackButton={false}
                    name={TransitionViewNames.ICSLINK_OUTLOOK_VIEW}
                    viewAnimation={viewAnimation}
                    initStyles={initStyles}
                >
                    <TitleHeader
                        widget={widget}
                        widgetTheme={widgetTheme}
                        title={translate(TranslationIds.CALENDAR_PROC_TITLE)}
                        subtitle={translate(TranslationIds.CALENDAR_GUIDE_OUTLOOK_DESKTOP_SUBTITLE)}
                    />
                    <Grid className={classes.body}>

                        <Grid className={classes.guideContentBody}>
                            <Grid className={classes.guideContent}>

                                <ImportantOutlook />

                                {/* Step 1: Copy your calendar link */}
                                <Grid className={classes.stepRow}>
                                    <span className={classes.stepBadge}>1</span>
                                    <Grid className={classes.stepContent}>
                                        <span className={classes.stepLabel}>
                                            {translate(TranslationIds.CALENDAR_GUIDE_STEP_COPY_LINK_LABEL)}
                                        </span>
                                        <Grid className={classes.linkSpace}>
                                            <span className={classes.webcalPrefix}>webcal://</span>
                                            <span className={classNames(['data-ecal-webcall-link', classes.link])}>
                                                {calendarURL.replace('webcal://', '')}
                                            </span>
                                            <span className={classes.linkDivider} />
                                            <CopyLinkButton
                                                inProgress={nextEnable}
                                                buttonClass={nextEnable ?
                                                    classNames([classes.copyButtonBase, classes.copyButtonSolid]) :
                                                    classNames([classes.copyButtonBase, classes.copyButtonOutlined])
                                                }
                                                iconClass={classes.copyLinkIcon}
                                                onClick={() => {
                                                    setNextEnable(true);
                                                    copyCalendarURL();
                                                }}
                                                copyLabel={translate(TranslationIds.CALENDAR_GUIDE_COPY_LINK)}
                                                copiedLabel={translate(TranslationIds.CALENDAR_GUIDE_COPIED)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* Step 2: Open Outlook Desktop */}
                                <Grid className={classes.stepRow}>
                                    <span className={classes.stepBadge}>2</span>
                                    <Grid className={classes.stepContent}>
                                        <span className={classes.stepLabel}>
                                            {translate(TranslationIds.CALENDAR_GUIDE_STEP_OPEN_OUTLOOK_LABEL)}
                                        </span>
                                        <span className={classes.stepDescription}>
                                            {translateWithTags(
                                                TranslationIds.CALENDAR_GUIDE_STEP_OPEN_OUTLOOK_DESCRIPTION,
                                                {
                                                    addcalendar: addCalendarText(),
                                                    subscribefromweb: subscribeFromWebText(),
                                                },
                                            )}
                                        </span>
                                    </Grid>
                                </Grid>

                                {/* Step 3: Paste and Import */}
                                <Grid className={classes.stepRow}>
                                    <span className={classes.stepBadge}>3</span>
                                    <Grid className={classes.stepContent}>
                                        <span className={classes.stepLabel}>
                                            {translate(TranslationIds.CALENDAR_GUIDE_STEP_PASTE_IMPORT_LABEL)}
                                        </span>
                                        <span className={classes.stepDescription}>
                                            {translateWithTags(
                                                TranslationIds.CALENDAR_GUIDE_STEP_PASTE_IMPORT_DESCRIPTION,
                                                {
                                                    webcalwarning: (
                                                        <span className={classes.webcalWarning}>
                                                            {translate(TranslationIds.CALENDAR_GUIDE_STEP_PASTE_IMPORT_WEBCAL_WARNING)}
                                                        </span>
                                                    ),
                                                    importlabel: importText(),
                                                },
                                            )}
                                        </span>
                                    </Grid>
                                </Grid>

                            </Grid>
                        </Grid>

                        <Grid className={classNames(['data-ecal-next-button-container', classes.nextButton])}>
                            <PrimaryButton
                                isDisabled={!nextEnable}
                                onClick={() => {
                                    appStore.gotoOutlookNewGuide();
                                    setNextEnable(false);
                                }}
                                textLabel={translate(TranslationIds.COMPONENT_NEXT)}
                            />
                        </Grid>

                        <Grid className={classes.help}>
                            { translate(TranslationIds.CALENDAR_PROC_NEED_HELP) }
                            <GuideLink
                                selectedCalendar={selectedCalendar}
                                label={translate(TranslationIds.CALENDAR_PROC_GUIDE)}
                                commonUIStyles={getCommonUIStyles({ widgetTheme, widget })}
                            />
                        </Grid>

                    </Grid>
                </WidgetDialog>
            </ContentContainer>
        </React.Fragment>
    );
}

ICSLinkOutlook.propTypes = {
    viewAnimation: PropTypes.bool,
    initStyles: PropTypes.any,
};

export default observer(ICSLinkOutlook);
